import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/feature/AppLayout";
import { useDashboardPageAccess } from "@/hooks/useDashboardPageAccess";
import { Banner } from "../settings/settingsUi";
import { usePersonalPreferences } from "./hooks/usePersonalPreferences";
import PersonalProfileSection from "./PersonalProfileSection";
import SecuritySettingsSection from "./SecuritySettingsSection";
import AppearanceSettingsSection from "./AppearanceSettingsSection";
import CannedResponsesSettingsSection from "./CannedResponsesSettingsSection";
import "./PersonalSettingsPage.css";

const BASE_SECTIONS = [
  { id: "general", label: "General", icon: "ri-user-3-line" },
  { id: "security", label: "Security", icon: "ri-shield-keyhole-line" },
  { id: "appearance", label: "Appearance", icon: "ri-palette-line" },
];

const SAVED_REPLIES_SECTION = { id: "saved-replies", label: "Saved replies", icon: "ri-bookmark-3-line" };

export default function PersonalSettingsPage() {
  const [active, setActive] = useState("general");
  const [profileBanner, setProfileBanner] = useState(null);
  const { canAccessPage, loading: pageAccessLoading } = useDashboardPageAccess();
  const showSavedReplies = !pageAccessLoading && canAccessPage("chat");
  const sections = useMemo(() => {
    if (!showSavedReplies) return BASE_SECTIONS;
    return [BASE_SECTIONS[0], SAVED_REPLIES_SECTION, ...BASE_SECTIONS.slice(1)];
  }, [showSavedReplies]);

  useEffect(() => {
    if (!showSavedReplies && active === "saved-replies") {
      setActive("general");
    }
  }, [showSavedReplies, active]);

  const prefs = usePersonalPreferences();
  const {
    loading,
    saving,
    displayName,
    setDisplayName,
    theme,
    error,
    saveProfile,
    saveTheme,
  } = prefs;

  const handleSaveProfile = async () => {
    const result = await saveProfile(displayName);
    if (result.ok) {
      setProfileBanner({ type: "ok", text: "Profile updated." });
    } else {
      setProfileBanner({ type: "err", text: result.message || "Could not save profile." });
    }
  };

  return (
    <AppLayout>
      <div className="personal-settings">
        <aside className="personal-settings__subnav ui-subnav" aria-label="Personal settings sections">
          <div className="personal-settings__subnav-header">
            <p className="text-[10px] font-bold uppercase tracking-widest text-teal-700 dark:text-teal-400">
              Account
            </p>
            <h2 className="personal-settings__mobile-header mt-1 text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
              Personal settings
            </h2>
          </div>

          <div className="personal-settings__tabs-mobile" role="tablist" aria-label="Personal settings">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={active === s.id}
                onClick={() => setActive(s.id)}
                className={`personal-settings__tab ${active === s.id ? "ui-pill-active" : "ui-pill-idle"}`}
              >
                <i className={s.icon} aria-hidden />
                {s.label}
              </button>
            ))}
          </div>

          <nav className="flex flex-col gap-0.5 px-3 pt-2 pb-4" aria-label="Personal settings">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-all ${
                  active === s.id ? "ui-pill-active" : "ui-pill-idle"
                }`}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/80 text-slate-500 shadow-sm ring-1 ring-slate-200/60 dark:bg-slate-800/90 dark:text-slate-400 dark:ring-slate-600">
                  <i className={s.icon} aria-hidden />
                </span>
                {s.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="personal-settings__content scrollbar-thin">
          <div className="personal-settings__content-inner">
            {error && active !== "appearance" && active !== "saved-replies" && (
              <div className="mb-4">
                <Banner type="err" text={error} />
              </div>
            )}

            {active === "general" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">General</h3>
                  <p className="mt-1 text-sm text-slate-500">Your profile for this workspace.</p>
                </div>
                <div className="ui-card-static !p-6 sm:!p-8">
                  <PersonalProfileSection
                    displayName={displayName}
                    onDisplayNameChange={setDisplayName}
                    onSave={handleSaveProfile}
                    saving={saving}
                    loading={loading}
                    banner={profileBanner}
                    onDismissBanner={() => setProfileBanner(null)}
                  />
                </div>
              </div>
            )}

            {active === "saved-replies" && showSavedReplies && <CannedResponsesSettingsSection />}

            {active === "security" && <SecuritySettingsSection />}

            {active === "appearance" && (
              <AppearanceSettingsSection
                savedTheme={theme}
                onSaveTheme={saveTheme}
                saving={saving}
                loadError={error}
              />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
